import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { Phone, CheckCircle2, Car, Calendar, AlertTriangle, ShieldCheck, Clock, ChevronRight, MapPin, FileCheck, Users, DollarSign } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Toaster, toast } from "sonner";

// For Vercel deployment - API is relative path
const API_SUBMIT_URL = '/api/submit';
const PHONE_NUMBER = "(864) 365-3248";
const PHONE_LINK = "tel:+18643653248";
const CALL_CTA_CLASS = "call-cta"; // Consistent class for all call buttons

// Google Ads Conversion Labels
// IMPORTANT: Replace these placeholder values with your actual Google Ads conversion labels
// You can find these in Google Ads: Tools & Settings > Conversions > [Your Conversion] > Tag setup
const GOOGLE_ADS_CONVERSION_ID = 'AW-17196366586';
const GOOGLE_ADS_FORM_SUBMIT_LABEL = 'HP5dCIPTq5AcEPr17odA';
const GOOGLE_ADS_CALL_CLICK_LABEL = 'sjZsCP6hrJAcEPr17odA';

// ---------------------------------------------------------------------------
// Law firm branding & legal placeholders (edit these values as needed)
// ---------------------------------------------------------------------------
const LAW_FIRM = "Rizzi Law Group";
const ATTORNEY_NAME = "[Attorney Name]";
const OFFICE_ADDRESS = "[Office Address]";
const RIZZI_DISCLOSURE = "Auto Accident Claim Assist is a marketing website for Rizzi Law Group. Submitting this form does not create an attorney-client relationship. Your information may be sent to Rizzi Law Group for review. Results vary.";

// GA4-ready tracking utility with Google Ads conversion support
const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === "undefined") return;

  // 1) GTM Custom Event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  });

  // 2) GA4 direct
  if (window.gtag) {
    window.gtag("event", eventName, eventParams);
  }

  console.log(`[TRACK] ${eventName}`, eventParams);

  // 3) Optional custom event
  window.dispatchEvent(
    new CustomEvent("conversion_event", {
      detail: { eventName, eventParams },
    })
  );
};

// Fire Google Ads conversion for successful form submissions
const trackFormConversion = () => {
  if (typeof window === "undefined" || !window.gtag) return;
  
  // Only fire if conversion label is configured (not placeholder)
  if (GOOGLE_ADS_FORM_SUBMIT_LABEL !== 'PASTE_FORM_SUBMIT_LABEL_HERE') {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_FORM_SUBMIT_LABEL}`,
    });
    console.log('[CONVERSION] Google Ads form submission conversion fired');
  } else {
    console.log('[CONVERSION] Form submit label not configured - skipping Google Ads conversion');
  }
};

// Fire Google Ads conversion for call button clicks
const trackCallConversion = (location) => {
  if (typeof window === "undefined" || !window.gtag) return;
  
  // Track the call click event (GA4 + GTM dataLayer)
  trackEvent('call_now_click', { location, phone: PHONE_NUMBER });
  
  // Fire Google Ads conversion for call clicks
  if (GOOGLE_ADS_CALL_CLICK_LABEL !== 'PASTE_CALL_CLICK_LABEL_HERE') {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CALL_CLICK_LABEL}`,
    });
    console.log('[CONVERSION] Google Ads call click conversion fired');
  } else {
    console.log('[CONVERSION] Call click label not configured - skipping Google Ads conversion');
  }
};

// Basic lead qualification based on intake answers
const classifyLead = (d) => {
  // Clearly not a fit
  if (d.in_south_carolina === "No") return "unqualified"; // outside service area
  if (d.hurt_in_accident === "No") return "unqualified"; // property damage only / no injury
  if (d.represented === "Yes") return "unqualified"; // already represented

  // Need more information
  if (d.represented === "Not sure" || d.at_fault === "Not sure") return "unclear";
  if (!d.in_south_carolina || !d.hurt_in_accident) return "unclear";

  // Qualified
  if (d.in_south_carolina === "Yes" && d.hurt_in_accident === "Yes" && d.represented === "No") {
    return "qualified";
  }
  return "unclear";
};

// Rizzi Law Group legal disclosure (shown near the hero form and in the footer)
const RizziDisclosure = ({ className = "" }) => (
  <p data-testid="rizzi-disclosure" className={`text-xs leading-relaxed ${className}`}>
    {RIZZI_DISCLOSURE}
  </p>
);

// Case example (only shown after the intake is completed, e.g. qualified result)
const CaseExample = () => (
  <div data-testid="case-example" className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left">
    <p className="text-slate-700 text-sm leading-relaxed">
      <span className="font-semibold text-navy">Case example:</span> A prior car accident client initially received an insurance offer of $800. After legal involvement, the case resolved for $12,000. Every case is different. Prior results do not guarantee a similar outcome.
    </p>
    <p className="text-slate-500 text-xs mt-2 leading-relaxed">
      Prior results do not guarantee a similar outcome. Every case depends on its own facts.
    </p>
  </div>
);


// Sticky Header Component
const StickyHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCallClick = () => {
    trackCallConversion('header');
  };

  return (
    <header
      data-testid="sticky-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-navy shadow-lg" : "bg-navy/95"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-8 w-8 text-gold" />
          <span className="font-serif text-white text-lg font-bold hidden sm:block">
            Auto Accident Claim Assist
          </span>
          <span className="font-serif text-white text-base font-bold sm:hidden">
            AACA
          </span>
        </div>

        {/* Call CTA (desktop + mobile) */}
        <a
          href={PHONE_LINK}
          onClick={handleCallClick}
          data-testid="header-call-btn"
          id="header-call-btn"
          className={`${CALL_CTA_CLASS} flex items-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-2 px-4 md:py-2.5 md:px-5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg text-sm md:text-base`}
        >
          <Phone className="h-4 w-4 md:h-5 md:w-5" />
          <span className="hidden sm:inline">Call Now: {PHONE_NUMBER}</span>
          <span className="sm:hidden">Call Now</span>
        </a>
      </div>
    </header>
  );
};

// Sticky Mobile Bottom Bar
const StickyMobileBar = () => {
  const handleCallClick = () => {
    trackCallConversion('mobile_bottom_bar');
  };

  return (
    <div 
      data-testid="sticky-mobile-bar"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] p-3"
    >
      <a
        href={PHONE_LINK}
        onClick={handleCallClick}
        data-testid="mobile-bar-call-btn"
        id="mobile-bar-call-btn"
        className={`${CALL_CTA_CLASS} w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-3.5 px-4 rounded-full transition-all shadow-md`}
      >
        <Phone className="h-5 w-5" />
        <span className="text-sm">Call Now: {PHONE_NUMBER}</span>
      </a>
      <p className="text-center text-slate-500 text-xs mt-2">No obligation · No upfront cost</p>
    </div>
  );
};

// Trust Bar Component
const TrustBar = () => {
  const trustItems = [
    { icon: <FileCheck className="h-4 w-4" />, text: "Free Case Review" },
    { icon: <DollarSign className="h-4 w-4" />, text: "No upfront cost" },
  ];

  return (
    <div data-testid="trust-bar" className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
      {trustItems.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5 text-slate-300 text-sm">
          <span className="text-gold">{item.icon}</span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  );
};

// Embedded Survey Component (for Hero)
const EmbeddedSurvey = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [surveyData, setSurveyData] = useState({
    hurt_in_accident: null,
    accident_date: null,
    at_fault: null,
    in_south_carolina: null,
    represented: null,
    first_name: "",
    email: "",
    phone: "",
    consent: false,
    honeypot: "" // Spam protection
  });
  const [specialMessage, setSpecialMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [qualificationResult, setQualificationResult] = useState(null);
  const surveyStartedRef = useRef(false);

  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;

  // Track survey start only once
  useEffect(() => {
    if (!surveyStartedRef.current) {
      surveyStartedRef.current = true;
      trackEvent('survey_start', { location: 'hero_embedded' });
    }
  }, []);

  const handleOptionSelect = (field, value) => {
    setSurveyData(prev => ({ ...prev, [field]: value }));
    setSpecialMessage(null);

    // Track step completion
    const stepMap = {
      hurt_in_accident: 1,
      accident_date: 2,
      at_fault: 3,
      in_south_carolina: 4,
      represented: 5
    };
    if (stepMap[field]) {
      trackEvent('survey_step_complete', { step: stepMap[field], field, value });
    }

    // Check for special messages
    if (field === "hurt_in_accident" && value === "No") {
      setSpecialMessage("We may still be able to help with property damage or next steps.");
    } else if (field === "at_fault" && value === "Yes") {
      setSpecialMessage("You may still have options.");
    } else if (field === "in_south_carolina" && value === "No") {
      setSpecialMessage("We currently focus on South Carolina claims.");
    } else if (field === "represented" && value === "Yes") {
      setSpecialMessage("If you already have a lawyer, we may not be able to review your case.");
    }
  };

  const handleDateQuickOption = (option) => {
    let dateString = "";
    const today = new Date();
    
    switch(option) {
      case "today":
        dateString = format(today, "PPP");
        break;
      case "1-7":
        dateString = "Within the past week";
        break;
      case "8-30":
        dateString = "8-30 days ago";
        break;
      case "30+":
        dateString = "More than 30 days ago";
        break;
      default:
        dateString = option;
    }
    
    setSurveyData(prev => ({ ...prev, accident_date: dateString }));
    trackEvent('survey_step_complete', { step: 2, field: 'accident_date', value: option });
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      setSurveyData(prev => ({ ...prev, accident_date: format(date, "PPP") }));
      setDatePickerOpen(false);
      trackEvent('survey_step_complete', { step: 2, field: 'accident_date', value: 'calendar_pick' });
    }
  };

  const nextStep = () => {
    setSpecialMessage(null);
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setSpecialMessage(null);
    setStep(prev => prev - 1);
  };

  // Phone input masking
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, "");
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setSurveyData(prev => ({ ...prev, phone: formatted }));
    validateField('phone', formatted);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    return digits.length === 10;
  };

  const validateField = (field, value) => {
    let error = null;
    switch(field) {
      case 'first_name':
        if (!value || value.trim() === '') error = 'First name is required';
        break;
      case 'email':
        if (!value || value.trim() === '') error = 'Email is required';
        else if (!validateEmail(value)) error = 'Please enter a valid email';
        break;
      case 'phone':
        if (!value || value.trim() === '') error = 'Phone number is required';
        else if (!validatePhone(value)) error = 'Please enter a valid 10-digit phone';
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, surveyData[field]);
  };

  const canProceed = () => {
    switch(step) {
      case 1:
        return surveyData.hurt_in_accident !== null;
      case 2:
        return surveyData.accident_date !== null;
      case 3:
        return surveyData.at_fault !== null;
      case 4:
        return surveyData.in_south_carolina !== null;
      case 5:
        return surveyData.represented !== null;
      case 6:
        return (
          surveyData.first_name.trim() !== "" &&
          validateEmail(surveyData.email) &&
          validatePhone(surveyData.phone) &&
          surveyData.consent &&
          surveyData.honeypot === "" // Spam check
        );
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    // Validate all fields
    const isValidName = validateField('first_name', surveyData.first_name);
    const isValidEmail = validateField('email', surveyData.email);
    const isValidPhone = validateField('phone', surveyData.phone);
    
    if (!isValidName || !isValidEmail || !isValidPhone || !surveyData.consent) {
      setTouched({ first_name: true, email: true, phone: true });
      return;
    }

    // Honeypot spam check
    if (surveyData.honeypot !== "") {
      console.log("Spam detected");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare payload with ALL form fields (flat structure for webhook)
      const payload = {
        first_name: surveyData.first_name.trim(),
        email: surveyData.email.trim(),
        phone: surveyData.phone,
        consent: surveyData.consent,
        hurt_in_accident: surveyData.hurt_in_accident,
        accident_date: surveyData.accident_date,
        at_fault: surveyData.at_fault,
        in_south_carolina: surveyData.in_south_carolina,
        represented: surveyData.represented,
        source: 'landing_page',
        submitted_at: new Date().toISOString(),
      };

      console.log('[FORM] Submitting to API:', payload);

      // Submit to Vercel serverless function
      const response = await fetch(API_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log('[FORM] API response:', result);

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Submission failed');
      }

      const qualification = classifyLead(surveyData);
      setQualificationResult(qualification);
      setIsSubmitted(true);
      trackEvent('contact_form_submit', {
        hurt_in_accident: surveyData.hurt_in_accident,
        in_south_carolina: surveyData.in_south_carolina,
        at_fault: surveyData.at_fault,
        represented: surveyData.represented,
      });
      trackEvent(`intake_${qualification}`, { qualification });
      
      // Track successful submission for GA4
      trackEvent('lead_submit', {
        hurt_in_accident: surveyData.hurt_in_accident,
        in_south_carolina: surveyData.in_south_carolina,
        at_fault: surveyData.at_fault
      });
      
      // Fire Google Ads conversion for successful form submission
      trackFormConversion();
      
      toast.success("Your information has been submitted successfully!");
      if (onComplete) onComplete();
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("There was an error submitting your information. Please try calling us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const OptionButton = ({ value, selected, onClick, children, stepId }) => (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`option-${stepId}-${value.toLowerCase().replace(/\s+/g, '-')}`}
      className={`w-full p-3 sm:p-4 rounded-xl border-2 text-left font-semibold transition-all text-sm sm:text-base ${
        selected
          ? "border-gold bg-gold/10 text-navy"
          : "border-slate-200 bg-white text-slate-700 hover:border-gold/50 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <span>{children}</span>
        {selected && <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0" />}
      </div>
    </button>
  );

  // Result screens shown after intake completion (routed by qualification)
  if (isSubmitted) {
    const CallNowButton = ({ testid }) => (
      <a
        href={PHONE_LINK}
        onClick={() => trackCallConversion(`result_${qualificationResult}`)}
        data-testid={testid}
        className={`${CALL_CTA_CLASS} flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-3.5 px-6 rounded-full transition-all shadow-lg w-full animate-pulse-gold`}
      >
        <Phone className="h-5 w-5" />
        Call Now: {PHONE_NUMBER}
      </a>
    );

    // Qualified result
    if (qualificationResult === 'qualified') {
      return (
        <div data-testid="result-qualified" className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in">
          <div className="text-center mb-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-serif text-xl font-bold text-navy mb-2">You may qualify for a case review</h3>
            <p className="text-slate-600 text-sm">
              Based on your answers, your case may be worth reviewing. Call now for faster help so {LAW_FIRM} can evaluate your claim.
            </p>
          </div>

          <div className="mb-4">
            <CaseExample />
          </div>

          <CallNowButton testid="result-qualified-call-btn" />

          <RizziDisclosure className="text-slate-400 mt-4" />
        </div>
      );
    }

    // Unclear result
    if (qualificationResult === 'unclear') {
      return (
        <div data-testid="result-unclear" className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="font-serif text-xl font-bold text-navy mb-2">We need a few more details</h3>
          <p className="text-slate-600 text-sm mb-4">
            Based on your answers, we need a little more information before confirming next steps. Call now and someone can review your situation.
          </p>
          <CallNowButton testid="result-unclear-call-btn" />
          <RizziDisclosure className="text-slate-400 mt-4" />
        </div>
      );
    }

    // Clearly unqualified result
    return (
      <div data-testid="result-unqualified" className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in text-center">
        <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-slate-500" />
        </div>
        <h3 className="font-serif text-xl font-bold text-navy mb-2">This may not be a fit</h3>
        <p className="text-slate-600 text-sm mb-4">
          Based on your answers, this may not be a case {LAW_FIRM} can review. If something was entered incorrectly or there are important details missing, you can still call.
        </p>
        <CallNowButton testid="result-unqualified-call-btn" />
        <RizziDisclosure className="text-slate-400 mt-4" />
      </div>
    );
  }

  return (
    <div data-testid="embedded-survey" className="bg-white rounded-2xl shadow-xl p-5 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg font-bold text-navy">Free Case Review</h3>
        <span className="text-xs text-slate-500 flex-shrink-0 ml-2">Step {step}/6</span>
      </div>
      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
        <Clock className="h-3 w-3" /> Handled by {LAW_FIRM} · Takes about 60 seconds
      </p>
      
      {/* Progress Bar */}
      <Progress value={progress} className="h-1.5 mb-4 bg-slate-200" data-testid="survey-progress" />

      {/* Step Content */}
      <div className="min-h-[220px] animate-slide-in" key={step}>
        {/* Step 1: Were you hurt? */}
        {step === 1 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <AlertTriangle className="h-5 w-5 text-gold flex-shrink-0" />
              Were you hurt in a car accident?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="Yes"
                stepId="q1"
                selected={surveyData.hurt_in_accident === "Yes"}
                onClick={(v) => handleOptionSelect("hurt_in_accident", v)}
              >
                Yes, I was injured
              </OptionButton>
              <OptionButton
                value="No"
                stepId="q1"
                selected={surveyData.hurt_in_accident === "No"}
                onClick={(v) => handleOptionSelect("hurt_in_accident", v)}
              >
                No, but I have other damages
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 2: When was the accident? */}
        {step === 2 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="h-5 w-5 text-gold flex-shrink-0" />
              When was the accident?
            </h4>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <OptionButton
                value="today"
                stepId="q2"
                selected={surveyData.accident_date === format(new Date(), "PPP")}
                onClick={() => handleDateQuickOption("today")}
              >
                Today
              </OptionButton>
              <OptionButton
                value="1-7"
                stepId="q2"
                selected={surveyData.accident_date === "Within the past week"}
                onClick={() => handleDateQuickOption("1-7")}
              >
                1-7 days
              </OptionButton>
              <OptionButton
                value="8-30"
                stepId="q2"
                selected={surveyData.accident_date === "8-30 days ago"}
                onClick={() => handleDateQuickOption("8-30")}
              >
                8-30 days
              </OptionButton>
              <OptionButton
                value="30+"
                stepId="q2"
                selected={surveyData.accident_date === "More than 30 days ago"}
                onClick={() => handleDateQuickOption("30+")}
              >
                30+ days
              </OptionButton>
            </div>
            
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="date-picker-trigger"
                  className="w-full justify-start text-left font-normal text-sm h-10"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Or pick a specific date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={surveyData.accident_date ? new Date(surveyData.accident_date) : undefined}
                  onSelect={handleCalendarSelect}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Step 3: Were you at fault? */}
        {step === 3 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Car className="h-5 w-5 text-gold flex-shrink-0" />
              Were you at fault?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="No"
                stepId="q3"
                selected={surveyData.at_fault === "No"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                No, I was not at fault
              </OptionButton>
              <OptionButton
                value="Yes"
                stepId="q3"
                selected={surveyData.at_fault === "Yes"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                Yes, I was at fault
              </OptionButton>
              <OptionButton
                value="Not sure"
                stepId="q3"
                selected={surveyData.at_fault === "Not sure"}
                onClick={(v) => handleOptionSelect("at_fault", v)}
              >
                I'm not sure
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 4: South Carolina? */}
        {step === 4 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <MapPin className="h-5 w-5 text-gold flex-shrink-0" />
              Was the accident in South Carolina?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="Yes"
                stepId="q4"
                selected={surveyData.in_south_carolina === "Yes"}
                onClick={(v) => handleOptionSelect("in_south_carolina", v)}
              >
                Yes, in South Carolina
              </OptionButton>
              <OptionButton
                value="No"
                stepId="q4"
                selected={surveyData.in_south_carolina === "No"}
                onClick={(v) => handleOptionSelect("in_south_carolina", v)}
              >
                No, in a different state
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 5: Already represented? */}
        {step === 5 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Users className="h-5 w-5 text-gold flex-shrink-0" />
              Do you already have a lawyer for this accident?
            </h4>
            <div className="space-y-2">
              <OptionButton
                value="No"
                stepId="q5"
                selected={surveyData.represented === "No"}
                onClick={(v) => handleOptionSelect("represented", v)}
              >
                No, not yet
              </OptionButton>
              <OptionButton
                value="Yes"
                stepId="q5"
                selected={surveyData.represented === "Yes"}
                onClick={(v) => handleOptionSelect("represented", v)}
              >
                Yes, I'm already represented
              </OptionButton>
              <OptionButton
                value="Not sure"
                stepId="q5"
                selected={surveyData.represented === "Not sure"}
                onClick={(v) => handleOptionSelect("represented", v)}
              >
                I'm not sure
              </OptionButton>
            </div>
            {specialMessage && (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                {specialMessage}
              </div>
            )}
          </div>
        )}

        {/* Step 6: Contact Information */}
        {step === 6 && (
          <div>
            <h4 className="font-semibold text-navy mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Phone className="h-5 w-5 text-gold flex-shrink-0" />
              Your Contact Information
            </h4>
            <div className="space-y-3">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={surveyData.honeypot}
                onChange={(e) => setSurveyData(prev => ({ ...prev, honeypot: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div>
                <Label htmlFor="first_name" className="text-slate-700 text-sm">First Name *</Label>
                <Input
                  id="first_name"
                  data-testid="input-first-name"
                  value={surveyData.first_name}
                  onChange={(e) => {
                    setSurveyData(prev => ({ ...prev, first_name: e.target.value }));
                    if (touched.first_name) validateField('first_name', e.target.value);
                  }}
                  onBlur={() => handleBlur('first_name')}
                  placeholder="John"
                  className={`mt-1 h-11 ${touched.first_name && errors.first_name ? 'border-red-500' : ''}`}
                />
                {touched.first_name && errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={surveyData.email}
                  onChange={(e) => {
                    setSurveyData(prev => ({ ...prev, email: e.target.value }));
                    if (touched.email) validateField('email', e.target.value);
                  }}
                  onBlur={() => handleBlur('email')}
                  placeholder="john@example.com"
                  className={`mt-1 h-11 ${touched.email && errors.email ? 'border-red-500' : ''}`}
                />
                {touched.email && errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-700 text-sm">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="input-phone"
                  value={surveyData.phone}
                  onChange={handlePhoneChange}
                  onBlur={() => handleBlur('phone')}
                  placeholder="(555) 123-4567"
                  className={`mt-1 h-11 ${touched.phone && errors.phone ? 'border-red-500' : ''}`}
                />
                {touched.phone && errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>
              <div className="flex items-start space-x-3 pt-1">
                <Checkbox
                  id="consent"
                  data-testid="checkbox-consent"
                  checked={surveyData.consent}
                  onCheckedChange={(checked) => setSurveyData(prev => ({ ...prev, consent: checked }))}
                  className="mt-0.5"
                />
                <Label htmlFor="consent" className="text-xs text-slate-600 leading-tight cursor-pointer">
                  I agree to be contacted by phone/text about my claim. Msg/data rates may apply. *
                </Label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-3 mt-4">
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={prevStep}
            data-testid="btn-back"
            className="flex-1 h-11"
          >
            Back
          </Button>
        ) : (
          <div className="flex-1" />
        )}
        
        {step < 6 ? (
          <Button
            onClick={nextStep}
            disabled={!canProceed()}
            data-testid="btn-next"
            className="flex-1 bg-gold hover:bg-gold-600 text-white h-11"
          >
            Continue
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canProceed() || isSubmitting}
            data-testid="btn-submit"
            className="flex-1 bg-gold hover:bg-gold-600 text-white h-11"
          >
            {isSubmitting ? "Submitting..." : "Get Free Review"}
          </Button>
        )}
      </div>
      
      {/* Friction reducers */}
      <div className="flex justify-center gap-4 mt-3 text-xs text-slate-500">
        <span>No obligation</span>
        <span>•</span>
        <span>No upfront cost</span>
      </div>
    </div>
  );
};

// Hero Section Component with Embedded Survey
const InsurerWarning = ({ testid = "insurer-warning", className = "" }) => (
  <div
    data-testid={testid}
    className={`bg-gold/10 border border-gold/40 rounded-xl p-4 flex items-start gap-3 ${className}`}
  >
    <span className="text-xl leading-none flex-shrink-0" aria-hidden="true">⚠️</span>
    <p className="text-slate-200 text-sm leading-relaxed text-left">
      <span className="font-bold text-white">Don't say the wrong thing.</span> The other driver's insurer may call within days of your crash — and one wrong statement can shrink or even wipe out your settlement. Never give a recorded statement or accept an offer until you know what your claim is really worth.
    </p>
  </div>
);

const HeroSection = () => {
  const handleCallClick = () => {
    trackCallConversion('hero');
  };

  return (
    <section data-testid="hero-section" className="bg-navy pt-20 pb-8 md:pt-24 md:pb-20">
      <div className="max-w-7xl mx-auto px-4">
        {/* Mobile: headline + subhead + CTAs, then form first */}
        <div className="md:hidden">
          <h1 className="font-serif text-3xl font-bold tracking-tight leading-tight mb-3 text-white text-center">
            Injured in a South Carolina Car Accident?
          </h1>
          <p className="text-slate-300 text-center text-sm mb-5">
            Get a free case review with Rizzi Law Group. No upfront cost. No obligation.
          </p>
          <InsurerWarning testid="insurer-warning-mobile" className="mb-6" />
          <div className="mb-6">
            <a
              href={PHONE_LINK}
              onClick={handleCallClick}
              data-testid="hero-call-btn-mobile"
              id="hero-call-btn-mobile"
              className={`${CALL_CTA_CLASS} w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-3.5 px-4 rounded-full transition-all shadow-lg`}
            >
              <Phone className="h-5 w-5" />
              Call Now: {PHONE_NUMBER}
            </a>
          </div>
          <div className="mb-3">
            <EmbeddedSurvey />
          </div>
          <RizziDisclosure className="text-slate-400 text-center text-[11px] leading-relaxed mb-6" />
        </div>

        {/* Desktop */}
        <div className="hidden md:grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white animate-fade-in">
            <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Injured in a South Carolina Car Accident?
            </h1>

            <p className="text-lg text-slate-300 mb-6">
              Get a free case review with Rizzi Law Group. No upfront cost. No obligation.
            </p>

            <TrustBar />

            <p className="text-slate-300 text-sm mt-6 mb-6 flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
              <span>Past client offer increased from $800 to $12,000. <span className="text-slate-400">Results vary.</span></span>
            </p>

            <InsurerWarning testid="insurer-warning" className="mb-8" />

            <div>
              <a
                href={PHONE_LINK}
                onClick={handleCallClick}
                data-testid="hero-call-btn"
                id="hero-call-btn"
                className={`${CALL_CTA_CLASS} inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-4 px-8 rounded-full transition-all hover:-translate-y-1 shadow-lg text-lg`}
              >
                <Phone className="h-6 w-6" />
                Call Now: {PHONE_NUMBER}
              </a>
            </div>
          </div>

          {/* Right: Embedded Survey */}
          <div>
            <EmbeddedSurvey />
            <RizziDisclosure className="text-slate-400 text-[11px] leading-relaxed mt-3" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Past Case Results Section
const CaseResultsSection = () => {
  const results = [
    {
      amount: "$1,850,000",
      label: "Head-On Collision",
      description: "Defendant crossed the center line and struck plaintiff's vehicle, causing severe injuries requiring numerous surgeries.",
    },
    {
      amount: "$500,000",
      label: "Rear-End Collision",
      description: "Plaintiff sustained severe injuries after a rear-end collision.",
    },
    {
      amount: "$445,000",
      label: "Car Wreck Injuries",
      description: "Plaintiff sustained numerous injuries from a car wreck.",
    },
    {
      amount: "$390,000",
      label: "Serious Injury / Surgery",
      description: "Plaintiff was injured in a serious car accident resulting in surgery.",
    },
  ];

  const scrollToForm = () => {
    trackEvent('survey_start', { location: 'case_results' });
    const hero = document.querySelector('[data-testid="hero-section"]');
    if (hero) {
      hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <section data-testid="case-results-section" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy text-center mb-3">
          Past Car Accident Case Results
        </h2>
        <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
          A selection of past car accident results handled by {LAW_FIRM}.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {results.map((r, i) => (
            <div
              key={i}
              data-testid={`case-result-card-${i + 1}`}
              className="bg-navy rounded-2xl p-6 md:p-7 flex flex-col shadow-sm hover:shadow-lg transition-shadow"
            >
              <p className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight">
                {r.amount}
              </p>
              <div className="flex items-center gap-2 mt-3 mb-4">
                <span className="w-5 h-0.5 bg-gold inline-block flex-shrink-0" />
                <span className="text-gold text-xs font-bold tracking-wider uppercase">
                  {r.label}
                </span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{r.description}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10">
          <button
            type="button"
            onClick={scrollToForm}
            data-testid="case-results-cta-btn"
            className="bg-gold hover:bg-gold-600 text-white font-bold uppercase tracking-wide py-4 px-10 rounded-full transition-all hover:-translate-y-0.5 shadow-lg"
          >
            Request Free Case Review
          </button>
        </div>

        <p className="text-slate-500 text-xs text-center leading-relaxed mt-8 max-w-2xl mx-auto">
          Past results do not guarantee a similar outcome. Each case is different and must be evaluated on its own facts, evidence, injuries, insurance coverage, and applicable law.
        </p>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      icon: <Car className="h-8 w-8" />,
      title: "Share a few details",
      description: "Answer simple questions about your accident, injuries, and insurance situation."
    },
    {
      icon: <ShieldCheck className="h-8 w-8" />,
      title: "We review your case",
      description: "Your information is securely reviewed to understand your circumstances."
    },
    {
      icon: <Phone className="h-8 w-8" />,
      title: "We call to discuss",
      description: "You'll receive a call to discuss your case and explain your options."
    },
    {
      icon: <CheckCircle2 className="h-8 w-8" />,
      title: "Case review by Rizzi Law Group",
      description: "Your information may be submitted to Rizzi Law Group for case review."
    }
  ];

  return (
    <section data-testid="how-it-works-section" className="py-12 md:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy text-center mb-3">
          How it Works
        </h2>
        <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
          Get the help you need in four simple steps. No upfront cost, no obligation.
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              data-testid={`step-card-${index + 1}`}
              className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow group"
            >
              <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center text-gold mb-4 group-hover:bg-gold group-hover:text-white transition-colors">
                {step.icon}
              </div>
              <div className="text-gold font-bold text-sm mb-2">Step {index + 1}</div>
              <h3 className="font-serif text-lg font-bold text-navy mb-2">{step.title}</h3>
              <p className="text-slate-600 text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "Do I need insurance to get help?",
      answer: "No, you don't necessarily need insurance to seek help after an accident. We can review your situation regardless of your insurance status and help determine your options, including UM/UIM coverage you might not be aware of."
    },
    {
      question: "What if I'm not sure who was at fault?",
      answer: "Fault can be complicated in auto accidents. A professional can review the circumstances of your accident and help explain possible paths forward. Many cases involve shared fault, and you may still have options."
    },
    {
      question: "What if injuries appeared later?",
      answer: "Many auto-related injuries show up days or weeks after an accident. This is very common, especially with soft-tissue injuries like whiplash. A review can still be helpful, and documenting these delayed injuries is important for your claim."
    },
    {
      question: "What if I already talked to an insurance adjuster?",
      answer: "You can still speak with someone to understand your options moving forward. It's important to know that insurance adjusters work for the insurance company, not for you. Getting independent advice can help protect your interests."
    },
    {
      question: "Is there any cost to start?",
      answer: "There's no upfront cost to have Rizzi Law Group review your potential claim. Many auto accident attorneys work on a contingency basis, meaning they typically only get paid if you recover on your claim."
    }
  ];

  return (
    <section data-testid="faq-section" className="py-12 md:py-20 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy text-center mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-slate-600 text-center mb-10">
          Get answers to common questions about auto accident claims.
        </p>
        
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              data-testid={`faq-item-${index + 1}`}
              className="bg-white rounded-xl border border-slate-200 px-6"
            >
              <AccordionTrigger className="text-left font-semibold text-navy hover:no-underline py-4 text-sm sm:text-base">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-4 text-sm">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const handleCallClick = () => {
    trackCallConversion('cta_section');
  };

  return (
    <section data-testid="cta-section" className="py-12 md:py-16 bg-navy">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <ShieldCheck className="h-12 w-12 text-gold mx-auto mb-4" />
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-3">
          Ready to Check Your Case?
        </h2>
        <p className="text-slate-300 mb-6">
          See if your accident may qualify for a free case review with Rizzi Law Group.
        </p>
        
        <div className="flex justify-center mb-4">
          <a
            href={PHONE_LINK}
            onClick={handleCallClick}
            data-testid="cta-call-btn"
            id="cta-call-btn"
            className={`${CALL_CTA_CLASS} flex items-center justify-center gap-2 bg-gold hover:bg-gold-600 text-white font-bold py-4 px-8 rounded-full transition-all hover:-translate-y-1 shadow-lg`}
          >
            <Phone className="h-5 w-5" />
            Call Now: {PHONE_NUMBER}
          </a>
        </div>
        
        <p className="text-slate-400 text-sm">
          No obligation · No upfront cost
        </p>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-navy-400 py-10 pb-24 md:pb-10">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-gold" />
            <span className="font-serif text-white text-base font-bold">
              Auto Accident Claim Assist
            </span>
          </div>
          <a
            href={PHONE_LINK}
            onClick={() => trackCallConversion('footer')}
            id="footer-call-btn"
            className={`${CALL_CTA_CLASS} flex items-center gap-2 text-white hover:text-gold transition-colors`}
          >
            <Phone className="h-5 w-5" />
            {PHONE_NUMBER}
          </a>
        </div>
        
        {/* Disclosure & Legal */}
        <div className="border-t border-slate-700 pt-6 space-y-3">
          <p className="text-slate-400 text-xs leading-relaxed" data-testid="footer-rizzi-disclosure">
            {RIZZI_DISCLOSURE}
          </p>
          <p className="text-slate-400 text-xs leading-relaxed" data-testid="footer-attorney-responsible">
            Attorney responsible for this advertising: {ATTORNEY_NAME}, {LAW_FIRM}, {OFFICE_ADDRESS}.
          </p>
          <p className="text-slate-400 text-xs leading-relaxed">
            <strong>Disclaimer:</strong> This website provides general information about auto accident claims in South Carolina and is not legal advice. Every case is unique, and prior results do not guarantee a similar outcome. Results depend on the specific facts of each case. By submitting your information, you consent to be contacted by phone, text, or email regarding your inquiry. Msg/data rates may apply. This is an attorney advertisement.
          </p>
        </div>
        
        <div className="border-t border-slate-700 mt-6 pt-4 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Auto Accident Claim Assist. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [showMobileBar] = useState(true);

  return (
    <div className="App min-h-screen">
      <Toaster position="top-center" richColors />
      <StickyHeader />
      <main>
        <HeroSection />
        <CaseResultsSection />
        <HowItWorksSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
      {showMobileBar && <StickyMobileBar />}
    </div>
  );
}

export default App;
